import React, { FC } from 'react'
import { Grid, Box, Flex } from '@moonpig/launchpad-components'
import { breakpoint, styled, css } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import { MegaNav } from '../MegaNav'
import { UtilityMenu } from '../UtilityMenu'
import { StoreSelector } from '../StoreSelector/StoreSelector'
import { ExperimentCreditAmount } from '../CreditAmount'
import { HeaderConfigProvider } from '..'
import { HeaderWrapper } from '../HeaderWrapper'
import { HEADER_MENU_ICON_WIDTH_PX } from '../constants'
import { HeaderProps } from './types'

const StyledHeaderWrapper = styled(HeaderWrapper)`
  isolation: isolate;
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  width: 100%;
  gap: ${spacingPx(2)};
  grid-template-areas:
    'menu   logo   storeLocator   utilities'
    'search search search search';

  ${breakpoint('md')} {
    gap: ${spacingPx(4)};
    grid-template-areas:
      'menu   logo      utilities'
      'search search storeLocator'
      '.      .                 .';
  }

  ${breakpoint('xl')} {
    grid-template-areas:
      'logo search  utilities'
      'menu menu storeLocator'
      '.    .               .';
  }
`

const StyledLogo = styled(Box)`
  z-index: 6;
  position: relative;
  grid-area: logo;
  grid-column: 4 / span 6;

  ${breakpoint('md')} {
    margin-left: ${HEADER_MENU_ICON_WIDTH_PX};
    grid-column: 1 / span 14;

    ${s({ mt: 3 })}
  }

  ${breakpoint('xl')} {
    margin-left: 0;
    margin-top: 0;
    grid-column: 1 / span 4;
  }

  ${s({ pl: { xs: 0, md: 6 } })}
`

const StyledStoreSelector = styled(Box)<{
  storeSelector: boolean
}>`
  grid-area: storeLocator;
  justify-content: end;
  display: flex;
  grid-column: 11 / span 3;

  ${breakpoint('md')} {
    grid-column: 19 / span 6;
  }

  ${breakpoint('lg')} {
    grid-column: 19 / span 6;
  }

  ${breakpoint('xl')} {
    ${({ storeSelector }) =>
      storeSelector ? 'grid-column: 20 / span 5;' : 'grid-column: none'}
    align-items: end;
  }
`

const StyledMenu = styled(Box)<{
  storeSelector: boolean
}>`
  grid-area: menu;
  grid-column: 1 / span 3;

  ${breakpoint('md')} {
    grid-column: 1 / span 0;
  }

  ${breakpoint('xl')} {
    ${({ storeSelector }) =>
      storeSelector ? 'grid-column: 1 / span 19;' : 'grid-column: 1 / span 24;'}
  }
`

const StyledUtilities = styled(Box)`
  grid-area: utilities;
  display: flex;
  justify-content: end;
  grid-column: 15 / span 9;

  ${breakpoint('md')} {
    grid-column: 13 / span 12;
  }

  ${breakpoint('lg')} {
    grid-column: 16 / span 9;
  }

  ${breakpoint('xl')} {
    grid-column: 18 / span 7;
  }
`

const StyledSearch = styled(Box)<{
  storeSelector: boolean
}>`
  grid-area: search;

  ${css`
    ${breakpoint('md')} {
      ${s({ px: 6, my: 4 })}
    }

    ${breakpoint('xl')} {
      ${s({ px: 6, my: 2 })}
    }
  `}

  grid-column: 1 / span 24;

  ${breakpoint('md')} {
    ${({ storeSelector }) =>
      storeSelector ? 'grid-column: 1 / span 18;' : 'grid-column: 1 / span 24;'}
  }

  ${breakpoint('lg')} {
    ${({ storeSelector }) =>
      storeSelector ? 'grid-column: 1 / span 18;' : 'grid-column: 1 / span 24;'}
  }

  ${breakpoint('xl')} {
    grid-column: 5 / span 13;
  }
`

export const Header: FC<React.PropsWithChildren<HeaderProps>> = ({
  logoLink,
  navItems,
  utilityItems,
  children,
  toggleAs,
  bannerText,
  notifier,
  nonStickyHeader,
  storeSelectorConfig,
  experimentCreditAmountConfig,
}) => (
  <HeaderConfigProvider
    storeSelectorConfig={storeSelectorConfig}
    experimentCreditAmountConfig={experimentCreditAmountConfig}
  >
    <StyledHeaderWrapper
      bannerText={bannerText}
      nonStickyHeader={nonStickyHeader}
    >
      <StyledGrid>
        <StyledLogo>{logoLink}</StyledLogo>
        <StyledSearch
          width="100%"
          storeSelector={!!storeSelectorConfig}
          data-testid="search-wrapper"
        >
          {children}
        </StyledSearch>
        <StyledUtilities>
          <UtilityMenu items={utilityItems} notifier={notifier} />
        </StyledUtilities>

        <StyledMenu storeSelector={!!storeSelectorConfig}>
          <MegaNav items={navItems} toggleAs={toggleAs} />
        </StyledMenu>

        <StyledStoreSelector storeSelector={!!storeSelectorConfig}>
          <Grid justifyContent="right">
            {storeSelectorConfig && (
              <Flex>
                <StoreSelector {...storeSelectorConfig} isHeaderSelector />
              </Flex>
            )}
            {experimentCreditAmountConfig && (
              <Box>
                <ExperimentCreditAmount {...experimentCreditAmountConfig} />
              </Box>
            )}
          </Grid>
        </StyledStoreSelector>
      </StyledGrid>
    </StyledHeaderWrapper>
  </HeaderConfigProvider>
)
